import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Network, Boxes, BookOpen, Copy, Check, ShieldCheck, Cpu, Webhook, Send, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card, CardHeader, EmptyState } from "@/components/common";

const CAP_STATUS = {
  active: "bg-st-success/10 text-st-success border-st-success",
  built: "bg-brand/10 text-brand border-brand",
  idea: "bg-surface-muted text-ink-muted border-border",
  deprecated: "bg-st-critical/10 text-st-critical border-st-critical",
};
const CAP_TYPE = { workflow: "Workflow", tool: "Tool", skill: "Skill", agent: "Agent" };

export default function AgentOS() {
  const [copied, setCopied] = useState(false);
  const { data: m, isLoading } = useQuery({
    queryKey: ["aos-manifest"],
    queryFn: async () => (await api.get("/agent-os/manifest")).data,
  });
  const { data: readme = "" } = useQuery({
    queryKey: ["aos-readme"],
    queryFn: async () => (await api.get("/agent-os/readme")).data,
  });

  if (isLoading || !m) return <div className="text-sm text-ink-muted">Loading…</div>;

  const agentsByLane = (m.agents || []).reduce((acc, a) => {
    (acc[a.lane] = acc[a.lane] || []).push(a);
    return acc;
  }, {});
  const laneLabel = Object.fromEntries((m.lanes || []).map((l) => [l.key, l.label]));

  const copy = () => {
    navigator.clipboard.writeText(readme);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div data-testid="agent-os-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.12em] uppercase text-ink-muted">
          <Network className="w-3.5 h-3.5 text-brand" /> Agent Operating System · v{m.aos_version}
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-ink mt-1">Any agent lands here — and knows exactly how to work.</h1>
        <p className="text-sm text-ink-muted mt-1 max-w-3xl">{m.purpose}</p>
      </div>

      {/* Locked rules */}
      <div className="bg-ink text-white p-5 mb-4" data-testid="aos-locked-rules">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4" style={{ color: "#7CA0FF" }} /><span className="font-heading text-sm font-semibold uppercase tracking-wide">Locked rules — non-negotiable</span></div>
        <ul className="grid md:grid-cols-2 gap-x-8 gap-y-1.5">
          {m.locked_rules.map((r, i) => <li key={i} className="text-[13px] leading-relaxed text-white/85 flex items-start gap-2"><span className="text-brand" style={{ color: "#7CA0FF" }}>—</span> {r}</li>)}
        </ul>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {[["Agents", m.agent_count, Cpu], ["Lanes", m.lanes.length, Network], ["Capabilities", m.capabilities.length, Boxes], ["Webhook routes", m.webhook_contract.length, Webhook]].map(([label, val, Icon]) => (
          <Card key={label}><div className="flex items-center gap-3"><Icon className="w-5 h-5 text-brand" /><div><div className="font-heading text-2xl font-bold text-ink leading-none">{val}</div><div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1">{label}</div></div></div></Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Capability registry */}
        <div className="lg:col-span-2 space-y-4">
          <DispatchPreview agents={m.agents} laneLabel={laneLabel} />
          <Card testId="aos-capabilities">
            <CardHeader title="Capability Registry" icon={Boxes} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-[11px] uppercase tracking-wider text-ink-muted border-b border-border">
                  <th className="text-left py-2 font-semibold">Capability</th>
                  <th className="text-left py-2 font-semibold">Type</th>
                  <th className="text-left py-2 font-semibold">Lanes</th>
                  <th className="text-left py-2 font-semibold">Status</th>
                  <th className="text-right py-2 font-semibold">¢/call</th>
                </tr></thead>
                <tbody>
                  {m.capabilities.map((c) => (
                    <tr key={c.id} className="border-b border-border/60" data-testid={`capability-row-${c.name}`}>
                      <td className="py-2.5 pr-2"><div className="font-medium text-ink">{c.branded_name || c.name}</div><div className="text-xs text-ink-muted font-mono">{c.name}</div></td>
                      <td className="py-2.5 pr-2 text-ink-muted">{CAP_TYPE[c.type] || c.type}</td>
                      <td className="py-2.5 pr-2"><div className="flex flex-wrap gap-1">{(c.lane || []).map((l) => <span key={l} className="text-[10px] bg-surface-muted text-ink-muted px-1.5 py-0.5 border border-border">{laneLabel[l] || l}</span>)}</div></td>
                      <td className="py-2.5 pr-2"><span className={`text-[10px] font-semibold uppercase tracking-wider border px-1.5 py-0.5 ${CAP_STATUS[c.status] || CAP_STATUS.idea}`}>{c.status}</span></td>
                      <td className="py-2.5 text-right font-mono text-ink-muted">{c.cost_per_call_cents ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Agents by lane */}
          <Card testId="aos-agents">
            <CardHeader title="Agent Roster (by lane)" icon={Cpu} />
            <div className="space-y-4">
              {Object.entries(agentsByLane).map(([lane, list]) => (
                <div key={lane} data-testid={`aos-lane-${lane}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-brand mb-1.5">{laneLabel[lane] || lane} · {list.length}</div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {list.map((a) => (
                      <div key={a.agent_id} className="border border-border px-3 py-2 flex items-center justify-between" data-testid={`aos-agent-${a.key}`}>
                        <div className="min-w-0"><div className="text-sm font-medium text-ink truncate">{a.name}</div><div className="text-xs text-ink-muted font-mono truncate">{a.key}</div></div>
                        <span className={`shrink-0 text-[10px] font-semibold uppercase tracking-wider border px-1.5 py-0.5 ${CAP_STATUS[a.status] || CAP_STATUS.idea}`}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* AGENTS.md + lanes */}
        <div className="space-y-4">
          <Card testId="aos-lanes">
            <CardHeader title="Lanes" icon={Network} />
            <ul className="space-y-2.5">
              {m.lanes.map((l) => (
                <li key={l.key}><div className="text-sm font-medium text-ink">{l.label} <span className="text-xs text-ink-muted font-mono">({l.key})</span></div><div className="text-xs text-ink-muted">{l.purpose}</div></li>
              ))}
            </ul>
          </Card>

          <Card testId="aos-readme">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-brand" /><h3 className="font-heading text-sm font-semibold uppercase tracking-tight">AGENTS.md</h3></div>
              <button onClick={copy} data-testid="aos-copy-readme" className="flex items-center gap-1.5 text-xs border border-border px-2 py-1 hover:bg-surface-muted transition-colors">
                {copied ? <><Check className="w-3.5 h-3.5 text-st-success" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
            </div>
            <pre className="text-[11px] leading-relaxed bg-surface-muted border border-border p-3 max-h-[420px] overflow-auto whitespace-pre-wrap text-ink-muted font-mono">{readme}</pre>
          </Card>
        </div>
      </div>
    </div>
  );
}

function DispatchPreview({ agents, laneLabel }) {
  const [agentId, setAgentId] = useState(agents?.[0]?.agent_id || "");
  const [directive, setDirective] = useState("Build the Harborview design concept and selections, ready for owner review.");
  const [projectRef, setProjectRef] = useState("");
  const [copied, setCopied] = useState(false);

  const mut = useMutation({
    mutationFn: async () => (await api.post("/agent-os/compose", {
      agent_id: agentId, directive, project_ref: projectRef || undefined,
    })).data,
  });
  const packet = mut.data;

  const copy = () => {
    navigator.clipboard.writeText(packet.prompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card testId="aos-dispatch-preview">
      <CardHeader title="Dispatch Packet Preview" icon={Send} />
      <p className="text-xs text-ink-muted mb-3">Pick an agent + directive to see the exact prompt, scoped files and allowed capabilities a build seat would receive — a QA check before any real run.</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-2">
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)} data-testid="dispatch-agent-select"
          className="border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none bg-white">
          {(agents || []).map((a) => <option key={a.agent_id} value={a.agent_id}>{a.name} · {laneLabel[a.lane] || a.lane}</option>)}
        </select>
        <input value={projectRef} onChange={(e) => setProjectRef(e.target.value)} placeholder="project_ref (optional)" data-testid="dispatch-project-ref"
          className="border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none" />
      </div>
      <textarea value={directive} onChange={(e) => setDirective(e.target.value)} rows={2} data-testid="dispatch-directive"
        className="w-full border border-border px-2 py-2 text-sm focus:border-brand focus:outline-none mb-2" placeholder="Directive…" />
      <button onClick={() => mut.mutate()} disabled={mut.isPending || !agentId || !directive.trim()} data-testid="dispatch-compose-btn"
        className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-semibold hover:bg-brand-dark disabled:opacity-60 transition-colors">
        {mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Compose packet
      </button>

      {packet && (
        <div className="mt-4 space-y-3" data-testid="dispatch-result">
          <div className="grid sm:grid-cols-3 gap-2 text-xs">
            <Meta label="Scoped files" value={packet.scoped_file_count + " / " + packet.max_context_items} />
            <Meta label="Capabilities" value={packet.allowed_capabilities.length} />
            <Meta label="Cost cap" value={packet.cost_cap_cents + "¢"} />
          </div>
          {packet.allowed_capabilities.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="dispatch-caps">{packet.allowed_capabilities.map((c) => <span key={c} className="text-[10px] bg-brand/10 text-brand border border-brand px-1.5 py-0.5 font-mono">{c}</span>)}</div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider text-ink-muted">Composed prompt · v{packet.prompt_version}</span>
            <button onClick={copy} data-testid="dispatch-copy" className="flex items-center gap-1.5 text-xs border border-border px-2 py-1 hover:bg-surface-muted transition-colors">
              {copied ? <><Check className="w-3.5 h-3.5 text-st-success" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy prompt</>}
            </button>
          </div>
          <pre className="text-[11px] leading-relaxed bg-ink text-white/90 p-3 max-h-[360px] overflow-auto whitespace-pre-wrap font-mono">{packet.prompt}</pre>
        </div>
      )}
    </Card>
  );
}

function Meta({ label, value }) {
  return <div className="border border-border px-3 py-2"><div className="font-heading text-lg font-bold text-ink leading-none">{value}</div><div className="text-[10px] uppercase tracking-wider text-ink-muted mt-1">{label}</div></div>;
}
